/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   helper functions to check current views.
 */

import { memoizeMaybeRouteProps, memoizeRouteProps } from '@zcode/memoizers'
import { type ZumperRoute, matchPath } from '@zcode/router-utils'
import type { RouteChildrenProps } from '@zcode/router-utils'
import { NZR_ALERTS, NZR_FAVORITES, NZR_HOMEPAGE, NZR_MESSAGED } from '../routes/constants'
import { detailRoutes, permanentAddressRoutes } from '../routes/routeDefinitions'

export const checkMainView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  if (!location) {
    return false
  }
  const { pathname } = location
  return (
    !pathname?.startsWith(NZR_FAVORITES) &&
    !pathname?.startsWith(NZR_ALERTS) &&
    !pathname?.startsWith(NZR_MESSAGED)
  )
})

export const checkDetailView = memoizeRouteProps((routeProps: RouteChildrenProps) => {
  const { location } = routeProps
  return !!(
    location?.pathname &&
    detailRoutes.some(
      (route: ZumperRoute) => location.pathname && matchPath(location.pathname, route)
    )
  )
})

export const checkPermanentAddressView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(
    location?.pathname &&
    permanentAddressRoutes.some(
      (route: ZumperRoute) => location.pathname && matchPath(location.pathname, route)
    )
  )
})

export const checkSavedSearchesView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!location?.pathname?.startsWith(NZR_ALERTS)
})

export const checkSavedSearchesDetailView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return Boolean(location?.pathname?.match(/\/alerts\/(?:\d+|all|new)/g))
})

export const checkHomepageView = memoizeMaybeRouteProps((routeProps) => {
  const { location } = routeProps
  return !!(location && location.pathname === NZR_HOMEPAGE)
})

export const checkFavoritesView = memoizeRouteProps(
  (/** @type {import('@zcode/memoizers').MaybeNoMatchRouteProps} */ routeProps) => {
    const { location } = routeProps

    return location?.pathname ? location.pathname.startsWith(NZR_FAVORITES) : false
  }
)

export const checkMessagedView = memoizeRouteProps((routeProps) => {
  const { location } = routeProps
  return location?.pathname ? location.pathname.startsWith(NZR_MESSAGED) : false
})

// Determine if search view is either favorites or messaged
export const checkInterestedView = memoizeRouteProps((routeProps) => {
  return (
    checkSavedSearchesView(routeProps) ||
    checkMessagedView(routeProps) ||
    checkFavoritesView(routeProps)
  )
})
